import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ScanAsset, ScanCustomer, ScanEventType, Stored } from "@/models/assetScan/AssetScanTypes";
import { EventDraft, validateEvent } from "@/models/assetScan/validateEvent";

interface Props {
  asset: Stored<ScanAsset>;
  types: ScanEventType[];
  customers: Stored<ScanCustomer>[];
  serialMismatch: boolean;
  saving: boolean;
  error?: string;
  onSave: (type: ScanEventType, draft: EventDraft) => void;
  onCancel: () => void;
}

/** Second screen: what happened to this asset. */
export function EventForm({ asset, types, customers, serialMismatch, saving, error, onSave, onCancel }: Props) {
  const { t } = useTranslation();
  const [typeKey, setTypeKey] = useState<string>();
  const [customerId, setCustomerId] = useState<string | undefined>(asset.details.state?.customerId);
  const [note, setNote] = useState("");
  const type = types.find(candidate => candidate.key === typeKey);
  const draft: EventDraft = { customerId: type?.customerEffect === "set" ? customerId : undefined, note };
  const problem = validateEvent(draft, type);
  const customerName = (id?: string) => customers.find(row => row.details.customerId === id)?.details.name ?? id;
  const state = asset.details.state;

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6">{asset.details.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {[asset.details.tag, asset.details.assetType].filter(Boolean).join(" · ")}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {state?.customerId
              ? t("assetScan.scan.atCustomer", { customer: customerName(state.customerId) })
              : t("assetScan.scan.noCustomer")}
          </Typography>
        </CardContent>
      </Card>
      {serialMismatch && <Alert severity="warning">{t("assetScan.scan.serialMismatch")}</Alert>}

      <ToggleButtonGroup
        orientation="vertical"
        exclusive
        fullWidth
        value={typeKey ?? null}
        onChange={(_, value: string | null) => setTypeKey(value ?? undefined)}
      >
        {types
          .filter(candidate => candidate.active)
          .map(candidate => (
            <ToggleButton key={candidate.key} value={candidate.key} sx={{ py: 1.5 }}>
              {candidate.name}
            </ToggleButton>
          ))}
      </ToggleButtonGroup>

      {type?.customerEffect === "set" && (
        <Autocomplete
          options={customers}
          value={customers.find(row => row.details.customerId === customerId) ?? null}
          onChange={(_, row) => setCustomerId(row?.details.customerId)}
          getOptionLabel={row => [row.details.name, row.details.city].filter(Boolean).join(", ")}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={params => <TextField {...params} label={t("assetScan.scan.customer")} />}
        />
      )}
      {type && (
        <TextField
          label={type.noteRequired ? t("assetScan.scan.noteRequired") : t("assetScan.scan.note")}
          multiline
          minRows={2}
          value={note}
          onChange={event => setNote(event.target.value)}
        />
      )}
      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={1}>
        <Button variant="outlined" fullWidth onClick={onCancel} disabled={saving}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="contained"
          fullWidth
          disabled={!!problem || saving}
          onClick={() => type && onSave(type, draft)}
        >
          {saving ? t("assetScan.scan.saving") : t("common.save")}
        </Button>
      </Stack>
      {type && problem && <Typography variant="caption" color="text.secondary">{t(`assetScan.scan.problem.${problem}`)}</Typography>}
    </Stack>
  );
}
